'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { listDiamonds, listCustomers, listSales, listMemos } from '@/lib/data';
import type { Customer, Diamond, Memo, Sale } from '@/lib/database';

interface SearchLists {
  diamonds: Diamond[];
  customers: Customer[];
  sales: Sale[];
  memos: Memo[];
}

interface SearchResult {
  id: string;
  type: 'diamond' | 'customer' | 'invoice' | 'memo' | 'cert';
  title: string;
  subtitle: string;
  href: string;
  badge?: string;
}

const TYPE_COLORS: Record<SearchResult['type'], string> = {
  diamond: 'bg-indigo-500/20 text-indigo-400',
  customer: 'bg-blue-500/20 text-blue-400',
  invoice: 'bg-emerald-500/20 text-emerald-400',
  memo: 'bg-amber-500/20 text-amber-400',
  cert: 'bg-purple-500/20 text-purple-400',
};

const TYPE_ICONS: Record<SearchResult['type'], string> = {
  diamond: 'CubeIcon',
  customer: 'UserCircleIcon',
  invoice: 'DocumentTextIcon',
  memo: 'ClipboardDocumentListIcon',
  cert: 'DocumentCheckIcon',
};

function search(query: string, lists: SearchLists): SearchResult[] {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  const results: SearchResult[] = [];

  // Diamonds
  lists.diamonds
    .filter(
      (d) =>
        d.sku.toLowerCase().includes(q) ||
        d.shape.toLowerCase().includes(q) ||
        d.color.toLowerCase().includes(q) ||
        d.clarity.toLowerCase().includes(q) ||
        d.cert_number?.toLowerCase().includes(q)
    )
    .slice(0, 4)
    .forEach((d) => {
      results.push({
        id: `d-${d.id}`,
        type: 'diamond',
        title: d.sku,
        subtitle: `${d.carat}ct ${d.color}/${d.clarity} ${d.shape} · $${d.price_usd.toLocaleString()}`,
        href: '/diamond-inventory',
        badge: d.status,
      });
      // Also add as cert if cert_number matches
      if (d.cert_number && d.cert_number.toLowerCase().includes(q)) {
        results.push({
          id: `cert-${d.id}`,
          type: 'cert',
          title: d.cert_number,
          subtitle: `${d.cert_body} · ${d.sku} · ${d.carat}ct ${d.color}/${d.clarity}`,
          href: '/certificates',
          badge: d.cert_body,
        });
      }
    });

  // Customers
  lists.customers
    .filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.company.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q)
    )
    .slice(0, 3)
    .forEach((c) => {
      results.push({
        id: `c-${c.id}`,
        type: 'customer',
        title: c.name,
        subtitle: `${c.company} · ${c.city}, ${c.country} · ${c.tier}`,
        href: '/crm',
        badge: c.tier,
      });
    });

  // Invoices / Sales
  lists.sales
    .filter(
      (s) =>
        s.invoice_number.toLowerCase().includes(q) ||
        s.customer_name.toLowerCase().includes(q) ||
        s.sku.toLowerCase().includes(q)
    )
    .slice(0, 3)
    .forEach((s) => {
      results.push({
        id: `inv-${s.id}`,
        type: 'invoice',
        title: s.invoice_number,
        subtitle: `${s.customer_name} · $${s.sale_price_usd.toLocaleString()} · ${s.sale_date}`,
        href: '/sales',
        badge: s.payment_status,
      });
    });

  // Memos
  lists.memos
    .filter(
      (m) =>
        m.memo_number.toLowerCase().includes(q) ||
        m.customer_name.toLowerCase().includes(q) ||
        m.sku.toLowerCase().includes(q)
    )
    .slice(0, 3)
    .forEach((m) => {
      results.push({
        id: `m-${m.id}`,
        type: 'memo',
        title: m.memo_number,
        subtitle: `${m.customer_name} · ${m.sku} · Due ${m.return_due_date}`,
        href: '/memos',
        badge: m.status,
      });
    });

  return results.slice(0, 12);
}

interface GlobalSearchProps {
  /** Mobile-sized trigger (no kbd hint, fills its container). Same modal. */
  compact?: boolean;
}

export default function GlobalSearch({ compact = false }: GlobalSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lists, setLists] = useState<SearchLists | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();

  useEffect(() => {
    setResults(lists ? search(query, lists) : []);
    setActiveIdx(0);
  }, [query, lists]);

  // Load searchable data the first time the dialog opens.
  useEffect(() => {
    if (!open || lists) return;
    let cancelled = false;
    Promise.all([listDiamonds(), listCustomers(), listSales(), listMemos()])
      .then(([diamonds, customers, sales, memos]) => {
        if (!cancelled) setLists({ diamonds, customers, sales, memos });
      })
      .catch(() => {
        // Search simply stays empty on failure
      });
    return () => {
      cancelled = true;
    };
  }, [open, lists]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      router.push(result.href);
      setOpen(false);
      setQuery('');
    },
    [router]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && results[activeIdx]) handleSelect(results[activeIdx]);
  };

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 50);
        }}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all duration-150 text-sm ${compact ? 'w-full min-w-0 text-xs' : 'min-w-[160px]'}`}
      >
        <Icon name="MagnifyingGlassIcon" size={14} />
        <span className="flex-1 text-left text-xs truncate">
          {compact ? 'Search…' : 'Search everything…'}
        </span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs bg-muted text-muted-foreground font-mono">
          ⌘K
        </kbd>
      </button>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <Icon
                name="MagnifyingGlassIcon"
                size={18}
                className="text-muted-foreground flex-shrink-0"
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search diamonds, customers, invoices, memos, certs…"
                className="flex-1 bg-transparent text-foreground text-sm placeholder-muted-foreground focus:outline-none"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 rounded text-muted-foreground hover:text-foreground"
                >
                  <Icon name="XMarkIcon" size={14} />
                </button>
              )}
              <kbd className="px-1.5 py-0.5 rounded text-xs bg-muted text-muted-foreground font-mono">
                Esc
              </kbd>
            </div>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto scrollbar-thin">
              {query.length < 2 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    Type at least 2 characters to search
                  </p>{' '}
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-4 px-2">
                    {(['diamond', 'customer', 'invoice', 'memo', 'cert'] as const).map((t) => (
                      <div key={t} className="flex items-center gap-1.5">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[t]}`}
                        >
                          {t}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : results.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground">
                    No results for "<span className="text-foreground">{query}</span>"
                  </p>
                </div>
              ) : (
                <div className="py-2">
                  {results.map((result, idx) => (
                    <button
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${idx === activeIdx ? 'bg-primary/10' : 'hover:bg-surface'}`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${TYPE_COLORS[result.type]}`}
                      >
                        <Icon
                          name={TYPE_ICONS[result.type] as Parameters<typeof Icon>[0]['name']}
                          size={14}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {result.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                      </div>
                      {result.badge && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-surface border border-border text-muted-foreground flex-shrink-0">
                          {result.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center gap-4 px-4 py-2.5 border-t border-border bg-surface/50">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <kbd className="px-1 py-0.5 rounded bg-muted font-mono">↑↓</kbd> navigate
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <kbd className="px-1 py-0.5 rounded bg-muted font-mono">↵</kbd> open
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <kbd className="px-1 py-0.5 rounded bg-muted font-mono">Esc</kbd> close
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
